import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import { StatusBadge, type Tone } from './StatusBadge';

export interface SettingsSectionProps {
  /** Section heading. */
  title: string;
  /** Short explanatory line under the title. */
  description?: ReactNode;
  /** Leading icon next to the title. */
  icon?: IconName;
  /** Icon color — defaults to the accent color. */
  iconColor?: string;
  /** Optional status/count badge shown after the title (e.g. "מוגדר", item count). */
  badge?: { label: ReactNode; tone?: Tone };
  /** Optional controls rendered at the end of the header row (e.g. a toggle). */
  action?: ReactNode;
  /**
   * Emphasized variant — thicker accent border + soft accent background, for the
   * one section on a tab that deserves the eye first (e.g. Caller-ID policy).
   */
  emphasized?: boolean;
  /**
   * These sections own their own save button and persist independently of the
   * page-level "שמור". When true, a subtle "נשמר בנפרד" hint is shown so the user
   * doesn't expect the bottom Save to cover them.
   */
  selfSaving?: boolean;
  children: ReactNode;
}

/**
 * Consistent card wrapper for a settings sub-section: icon + title + optional
 * badge/action in the header, an optional description, then the body. Replaces
 * the header markup that used to be copy-pasted across every settings section.
 */
export function SettingsSection({
  title, description, icon, iconColor, badge, action, emphasized, selfSaving, children,
}: SettingsSectionProps) {
  return (
    <section
      style={{
        border: emphasized ? '2px solid var(--accent)' : '1px solid var(--line)',
        borderRadius: 'var(--r-3)',
        background: emphasized ? 'var(--accent-soft, var(--surface-2))' : 'var(--surface)',
        padding: 'var(--pad-card)',
        marginBlock: 4,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: description ? 4 : 12 }}>
        {icon && <Icon name={icon} size={18} color={iconColor ?? 'var(--accent)'} />}
        <strong style={{ fontSize: 'var(--t-lg)' }}>{title}</strong>
        {badge && <StatusBadge tone={badge.tone ?? 'neutral'} size="sm">{badge.label}</StatusBadge>}
        {selfSaving && (
          <span
            title="הסקציה הזו נשמרת בכפתור שלה, לא בכפתור השמירה הראשי של הטאב"
            style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 3 }}
          >
            <Icon name="info" size={12} /> נשמר בנפרד
          </span>
        )}
        {action && <span style={{ marginInlineStart: 'auto' }}>{action}</span>}
      </div>
      {description && (
        <p style={{ margin: '0 0 12px', fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>{description}</p>
      )}
      {children}
    </section>
  );
}
